import { useState, useEffect, useRef, useCallback } from "react";

interface UseLocalRegeneratingOptions {
  /**
   * The current proposed answer string from the server. When this changes away
   * from the value captured at `startRegen` time, the flag is cleared.
   */
  proposedAnswer: string | null | undefined;
  /**
   * Current message ID. When this changes, the flag is reset immediately
   * (user navigated to a different message mid-generation).
   */
  messageId: string | null | undefined;
  /**
   * Called when the 90 s safety timeout fires. Callers can use this to
   * auto-retry or surface an error. The flag is already cleared before the
   * callback fires.
   */
  onTimeout?: () => void;
}

interface UseLocalRegeneratingReturn {
  isLocalRegenerating: boolean;
  /** Directly set the flag — useful for callers with additional clear conditions. */
  setIsLocalRegenerating: React.Dispatch<React.SetStateAction<boolean>>;
  /**
   * Call at the moment the generate mutation is fired. Captures `currentAnswer`
   * so the clearing effect knows when the new answer has actually arrived.
   */
  startRegen: (currentAnswer: string | null | undefined) => void;
}

/**
 * Manages the "local regenerating" flag that bridges the gap between:
 * - the generate mutation returning (Jupiter only queues a task), and
 * - the new proposed answer actually arriving in the UI.
 *
 * Handles three clearing conditions:
 * 1. The proposed answer string changed from when startRegen was called.
 * 2. The message ID changed (user navigated away mid-generation).
 * 3. 90 s safety timeout (silent backend failure).
 *
 * Callers with additional clearing conditions (e.g. Neptune's streaming
 * subscription) can call setIsLocalRegenerating(false) directly.
 */
export function useLocalRegenerating({
  proposedAnswer,
  messageId,
  onTimeout,
}: UseLocalRegeneratingOptions): UseLocalRegeneratingReturn {
  const [isLocalRegenerating, setIsLocalRegenerating] = useState(false);
  const regenStartedAnswerRef = useRef<string | null | undefined>(undefined);
  const onTimeoutRef = useRef(onTimeout);
  useEffect(() => { onTimeoutRef.current = onTimeout; });

  const startRegen = useCallback((currentAnswer: string | null | undefined) => {
    regenStartedAnswerRef.current = currentAnswer;
    setIsLocalRegenerating(true);
  }, []);

  // Clear when the answer string changes — the new generation has landed.
  useEffect(() => {
    if (!isLocalRegenerating) return;
    if (regenStartedAnswerRef.current === undefined) return;
    if (proposedAnswer !== regenStartedAnswerRef.current) {
      setIsLocalRegenerating(false);
    }
  }, [isLocalRegenerating, proposedAnswer]);

  // Reset when the user navigates to a different message.
  useEffect(() => {
    setIsLocalRegenerating(false);
    regenStartedAnswerRef.current = undefined;
  }, [messageId]);

  // Safety timeout: if the answer never changes within 90 s the backend task
  // likely died silently. Clear the flag and let the caller decide what to do.
  useEffect(() => {
    if (!isLocalRegenerating) return;
    const timer = setTimeout(() => {
      setIsLocalRegenerating(false);
      onTimeoutRef.current?.();
    }, 90_000);
    return () => clearTimeout(timer);
  }, [isLocalRegenerating]);

  return { isLocalRegenerating, setIsLocalRegenerating, startRegen };
}
